// <copyright file="RsuCoverage.tsx">
// © 2023 Florida Department of Transportation
//
// The copyright to the computer program(s) and source code herein is
// the property of the Florida Department of Transportation.
// The program(s) and source code may be used and/or copied only with the
// written permission of the Florida Department of Transportation or
// in accordance with the terms and conditions stipulated in the
// agreement/contract under which the program(s) and source code have
// been supplied.
// </copyright>

/* eslint-disable @typescript-eslint/no-explicit-any */

import bbox from "@turf/bbox";
import { featureCollection } from "@turf/helpers";
import axios from "axios";
import { default as dayjs } from "dayjs";
import { default as LEAF, latLng } from "leaflet";
import { Button } from "primereact/button";
import { Calendar } from "primereact/calendar";
import { Checkbox } from "primereact/checkbox";
import { Column } from "primereact/column";
import { DataTable } from "primereact/datatable";
import {Dialog } from "primereact/dialog";
import { MultiSelect } from "primereact/multiselect";
import { OverlayPanel } from "primereact/overlaypanel";
import { Toast } from "primereact/toast";
import React, { useEffect, useRef, useState as _useState, useContext } from "react";
import {
    MapContainer,
    TileLayer,
    LayersControl,
    LayerGroup,
    ScaleControl,
} from "react-leaflet";
import LoadingOverlay from "react-loading-overlay-ts";
import BasicSafetyMessageGeoJson from "src/components/VIPER/BasicSafetyMessageGeoJson/BasicSafetyMessageGeoJson";
import MarkerClusterGroup from "src/components/VIPER/MarkerClusterGroup/MarkerClusterGroup";
import RoadSideUnitGeoJson from "src/components/VIPER/RoadSideUnitGeoJson/RoadSideUnitGeoJson";
import styles from "src/components/VIPER/RsuCoverage/RsuCoverage.module.css";
import rsuImage from "src/components/VIPER/images/RSU_Icon_MarkerOrange.png";
import rsuCoverageImage from "src/components/VIPER/images/RsuCoverageLogo.png";
import ConfigContext from "src/context/ConfigContext";
import { getCookieJsonField, LoadingStatus } from "src/utils/SharedUtils";
import { v4 as uuidv4 } from "uuid";
import type { FeatureCollection } from "@turf/helpers";
import type { AxiosRequestConfig } from "axios";
import type { Dayjs } from "dayjs";
import type { CalendarChangeParams } from "primereact/calendar";
import type { FC } from "react";
import type {
    RsuAgencies,
    RsuAgency,
} from "src/components/VIPER/RoadSideUnitGeoJson/RoadSideUnitGeoJson";
import "leaflet/dist/leaflet.css";
import "leaflet.markercluster/dist/MarkerCluster.css";
import "leaflet.markercluster/dist/MarkerCluster.Default.css";
import type { IConfig } from "src/utils/ConfigLoader";

// Properties to initialize this UI component
interface RsuCoverageProps {
    useState?: typeof _useState; // optional hook override (for tests)
    apiBaseUrl: string;
}
interface SelectedCell {
    field: string;
    value: string | boolean | null;
    rowIndex: number;
}

const unknownAgencyOrDevice = "Unknown"; // Unknown agency/device filter item

// Info/error message popup
const toast = React.createRef<Toast>();

// Map layer select legend properties
const RSULegend = `<div style="position: relative; display: inline-block"><img src=${rsuImage} alt="icon-rsu" style="width:40px;height:40px;background: transparent;"/></div>`;
const RSUCoverageLegend = `<div style="position: relative; display: inline-block"><img src=${rsuCoverageImage} alt="icon-polygon" style="width:40px;height:40px;background: transparent;"/></div>`;

function handleDataFetchError(route: string, error: any) {
    const summary = `Error fetching ${route}`;
    let detail = error.response
        ? error.response.data.errorMessage
        : error.message;
    detail = JSON.stringify(detail);
    toast?.current?.show({
        severity: "error",
        summary: summary,
        detail: detail,
        sticky: true, // Show until dismissed by user
    });
    console.log(`${summary}: ${detail}`);
}

async function fetchAllData(props: RsuCoverageProps, dateFilter: Date) {
    console.debug("Fetching all data...");
    const dataRoutes = ["/rsu/agency-list", "/rsu/coverage-polygons", "/rsu"];
    const routes = dataRoutes.map((route) =>
        prepareUrl(props.apiBaseUrl, route)
    );
    const configs = dataRoutes.map((route) =>
        prepareRequest(route, dateFilter)
    );
    const emptyFeatureCollection: FeatureCollection = {
        type: "FeatureCollection",
        features: [],
    };
    const data: any[] = [null, emptyFeatureCollection, emptyFeatureCollection];
    await Promise.allSettled([
        axios.get<RsuAgencies>(routes[0], configs[0]),
        axios.get<FeatureCollection>(routes[1], configs[1]),
        axios.get<FeatureCollection>(routes[2], configs[2]),
    ]).then((results) => {
        results.forEach((result, idx) => {
            if (result.status === "rejected" || !result.value?.data) {
                console.log(result);
                const errorMessage =
                    "reason" in result ? result.reason : "Data not found";
                handleDataFetchError(routes[idx], errorMessage);
            } else {
                console.log(`Fetched ${result.value.request.responseURL}`);
                data[idx] = result.value.data;
            }
        });
    });
    return data;
}

function prepareUrl(baseUrl: string, apiRoute: string): string {
    const fetchRoute = apiRoute.startsWith("/") ? apiRoute : `/${apiRoute}`;
    const baseUri = baseUrl.endsWith("/")
        ? baseUrl.substring(0, baseUrl.length - 1)
        : baseUrl;
    return `${baseUri}${fetchRoute}`;
}

function prepareRequest(apiRoute: string, dateFilter: Date) {
    // Initialize url, headers, and query params
    const fetchRoute = apiRoute.startsWith("/") ? apiRoute : `/${apiRoute}`;
    const headers = {
        Authorization: `Bearer ${getCookieJsonField(
            "DepSessionIdentity",
            "token"
        )}`,
    };
    const queryParams: any = undefined;
    const config: AxiosRequestConfig<any> = {
        params: queryParams,
        headers: headers,
    };
    switch (fetchRoute) {
        case "/rsu/agency-list":
            break;
        case "/rsu":
            break;
        case "/rsu/coverage-polygons":
            config.params = {
                timeWindowStartDate: dayjs(dateFilter).format("YYYYMMDD"),
            };
            break;
        default:
            delete config.headers; // Remove auth header for unknown routes to be safe
            break;
    }

    return config;
}

function zoomToFeatures(
    map: LEAF.Map | null,
    bsmData: FeatureCollection,
    rsuData: FeatureCollection,
    selectedRSUs: string[]
) {
    if (!map) {
        return; // Map is not ready
    }

    let allFeatures;
    if (selectedRSUs.length == 0) {
        allFeatures = featureCollection(
            bsmData.features.concat(rsuData.features)
        );
    } else {
        allFeatures = featureCollection(
            bsmData.features
                .filter((feature) =>
                    selectedRSUs.includes(feature.properties?.sourceDeviceId)
                )
                .concat(
                    rsuData.features.filter((feature) =>
                        selectedRSUs.includes(
                            feature.properties?.sourceDeviceId
                        )
                    )
                )
        );
    }

    if (allFeatures.features.length == 0) {
        // Applied data filter yields no results, use a default location & zoom
        const mapCenter = latLng(28.5, -81.88); // Orlando
        const zoom = 8;
        map.setView(mapCenter, zoom);
        return;
    }

    // Create bounding box for all features within filters
    const featureBbox = bbox(allFeatures);
    if (!featureBbox) {
        return; // Could not form bbox for features
    }

    // Zoom map using the bounding box
    // bbox format is [minX, minY, maxX, maxY]
    const southwest = new LEAF.LatLng(featureBbox[1], featureBbox[0]);
    const northeast = new LEAF.LatLng(featureBbox[3], featureBbox[2]);
    map.fitBounds(new LEAF.LatLngBounds(southwest, northeast), {
        padding: LEAF.point(50, 50),
        animate: true,
    });
}

const RsuCoverage: FC<RsuCoverageProps> = (props: RsuCoverageProps) => {
    const configData = useContext(ConfigContext) as IConfig;
    const useState = props.useState ?? _useState;

    // Map settings & references
    const [map, setMap] = useState<LEAF.Map | null>(null); // Reference to the map container
    const [, setBsmLayer] = useState<LEAF.LayerGroup | null>(null); // Reference to BSM layer group
    const [, setRsuLayer] = useState<LEAF.LayerGroup | null>(null); // Reference to RSU layer group
    const [key] = useState<string>(uuidv4()); // Unique identifier for map state
    const [rsuUpdateKey, setRsuUpdateKey] = useState<string>(uuidv4()); // Unique identifier for rsu state
    const op = useRef<OverlayPanel>(null);

    // Date picker options
    const current = dayjs(); // Current day and time
    const maxDate: Dayjs = current.subtract(current.day() + 7, "day"); // Max selected value, Sunday of last week
    const minDate: Dayjs = maxDate.subtract(365, "day"); // Min selected value, roughly 1 year ago
    const [weekStart, setWeekStart] = useState<Date>(maxDate.toDate()); // Selected start date
    const [weekEnd, setWeekEnd] = useState<Date>(
        maxDate.add(6, "day").toDate()
    ); // Selected end date (calculated)

    // User input filters
    const [selectedDate, setSelectedDate] = useState<Date>(maxDate.toDate()); // Date selected by user input
    const [selectedAgency, setSelectedAgency] = useState<string[] | null>(null); // Agency selected by user input
    const [selectedRSUs, setSelectedRSUs] = useState<string[]>([]); // RSUs selected by user input
    const [selectedAgencyAll, setSelectedAgencyAll] = useState<boolean>(true);
    const [selectedDeviceAll, setSelectedDeviceAll] = useState<boolean>(true);
    const [availableRSUs, setAvailableRSUs] = useState<string[]>([]); // RSUs selected by user input
    const [includeOutliersChecked, setIncludeOutliersChecked] =
        useState<boolean>(false);

    // Externally loaded data
    const emptyFeatureCollection: FeatureCollection = {
        type: "FeatureCollection",
        features: [],
    };
    const [agencyData, setAgencyData] = useState<RsuAgencies | null>(null);
    const [bsmData, setBsmData] = useState<FeatureCollection>(
        emptyFeatureCollection
    );
    const [rsuData, setRsuData] = useState<FeatureCollection>(
        emptyFeatureCollection
    );
    const [colorMap, setColorMap] = useState<Map<string, string>>(
        new Map<string, string>()
    );
    const [deviceLabels, setDeviceLabels] = useState<Map<string, string>>(
        new Map<string, string>()
    );
    const [dataLoadStatus, setDataLoadStatus] = useState(LoadingStatus.loading);

    const [rsuConfigurations, setRsuConfigurations] = useState<object[]>();

    // Table components
    const [columns, setColumns] = useState<string[]>();
    const [selectedCell, setSelectedCell] = useState<SelectedCell | null>(); // Track selected cell data
    const [dialogVisible, setDialogVisible] = useState(false); // Dialog visibility

    // Hook on date filter state changes to reload data;
    // useEffect should be used SPARINGLY due to CASCADE potential
    useEffect(() => {
        // Load all data for map layers
        const fetchData = async () => {
            setDataLoadStatus(LoadingStatus.loading);
            const data = await fetchAllData(props, selectedDate);
            const dataAgency = data[0] as RsuAgencies;
            const dataBsm = data[1] as FeatureCollection;
            const dataRsu = data[2] as FeatureCollection;

            // Set RSU coverage polygon color
            const colorMapper = new Map<string, string>();
            const devicesWithPolygon: Set<string> = new Set(
                dataBsm.features.flatMap((f) => f.properties?.sourceDeviceId) ||
                    []
            );
            const devicesWithoutPolygon: Set<string> = new Set(
                dataRsu.features
                    .flatMap((f) => f.properties?.sourceDeviceId)
                    .filter((id) => !devicesWithPolygon.has(id)) || []
            );
            devicesWithPolygon.forEach((id) => {
                // Assign random color to RSU markers with coverage polygons
                colorMapper.set(
                    id,
                    "#" + Math.floor(Math.random() * 16777215).toString(16)
                );
            });
            devicesWithoutPolygon.forEach((id) => {
                // Default color to orange for RSU markers without coverage polygons
                colorMapper.set(id, "orange");
            });

            // Build device filter items
            const deviceFilterItems = new Map<string, string>();
            dataRsu.features.forEach((f) => {
                deviceFilterItems.set(
                    f.properties?.sourceDeviceId,
                    `${f.properties?.agency} - ${
                        f.properties?.deploymentProject
                    } - ${f.properties?.sourceDeviceId.slice(0, 7)}...`
                );
            });

            // Add devices from unknown agencies
            const unknownDevices: Set<string> = new Set(
                dataBsm.features
                    .filter((f) => !f.properties?.agency)
                    .flatMap((f) => f.properties?.sourceDeviceId) || []
            );
            console.log(
                `Devices with unknown agency: ${Array.from(unknownDevices)}`
            );
            if (unknownDevices.size > 0) {
                dataAgency?.rsus.push({
                    agencyName: unknownAgencyOrDevice,
                    sourceDeviceIds: Array.from(unknownDevices),
                });
                unknownDevices.forEach((deviceId) => {
                    deviceFilterItems.set(
                        deviceId,
                        `${unknownAgencyOrDevice} - ${unknownAgencyOrDevice} - ${deviceId.slice(
                            0,
                            7
                        )}...`
                    );
                });
            }
            const sortedDevices = new Map<string, string>(
                [...deviceFilterItems.entries()].sort((a, b) => {
                    // Sort map by value
                    if (a[1] > b[1]) {
                        return 1;
                    } else if (a[1] < b[1]) {
                        return -1;
                    } else {
                        return 0;
                    }
                })
            );

            // Cascade a batch of state updates
            const allAgencyNames = dataAgency?.rsus.flatMap(
                (r) => r.agencyName
            );
            setAgencyData(dataAgency);
            setBsmData(dataBsm);
            setRsuData(dataRsu);
            setColorMap(colorMapper);
            setDeviceLabels(sortedDevices);
            setAvailableRSUs(Array.from(deviceFilterItems.keys())); // Use sorted labels
            if (!selectedAgency) {
                // Set default filter for all agencies
                // useEffect CASCADES
                setSelectedAgency(allAgencyNames);
            } else {
                // Remove non-existent agencies from selected list
                // useEffect CASCADES
                setSelectedAgency(
                    selectedAgency.filter((a) => allAgencyNames.includes(a))
                );
            }
            setDataLoadStatus(LoadingStatus.loaded);
        };

        fetchData();
    }, [selectedDate]);

    // Hook on device filter state changes to zoom map;
    // useEffect should be used SPARINGLY due to CASCADE potential
    useEffect(() => {
        try {
            zoomToFeatures(map, bsmData, rsuData, selectedRSUs || []);
        } catch (error) {
            console.error(error);
        }
    }, [selectedRSUs]);

    // Map rsuData features to their properties
     useEffect(() => {
        const data = rsuData.features
            .filter((feature) => selectedRSUs.includes(feature.properties?.sourceDeviceId)) // Filter selected RSUs
            .map((feature) => feature.properties)
            .map((row) => {
                if(!row)
                    return {};
                return Object.fromEntries(Object.entries(row).map(([key, value]) => [key, value ?? "N/A"]));
            });

        setRsuConfigurations(data); // Update row data

        // Dynamically generate column fields from the keys of the first row (if any)
        if (data.length > 0 && data[0] !== null) {
            const colKeys = Object.keys(data[0]);
            setColumns(colKeys);
        } else {
            setColumns([]); // No columns if no data
        }
    }, [rsuData, selectedRSUs]);

    const headerMapping: {[key:string]: string} = {
        name: "Name",
        sourceDeviceId: "Source Device ID",
        agency: "Agency",
        deploymentProject: "Deployment Project",
        intersectionId: "Intersection ID",
        serialNumber: "Serial Number",
        cvMessageForwarder: "CV Message Forwarder",
        deviceOwner: "Device Owner",
        manufacturer: "Manufacturer",
        model: "Model",
        firmwareVersion: "Firmware Version",
        bsmTransmitting: "BSM Transmitting",
        timTransmitting: "TIM Transmitting",
        mapTransmitting: "Map Transmitting",
        spatTransmitting: "SPaT Transmitting",
        ifmTransmitting: "IFM Transmitting",
        notes: "Notes",
        default: "N/A"
    }

    const handleCellDoubleClick = (rowData: {[x: string]: string}, field: string, rowIndex: number) => {
        setSelectedCell({
            rowIndex: rowIndex, // Index of the clicked row
            field: (field !== null ? field : "N/A"), // Database field name
            value: rowData[field], // Value of the cell
        });
        setDialogVisible(true);
    };

    const cellBodyTemplate = (rowData: { [x: string]: string; }, { field }: { field: string; }, rowIndex: number) => {

        let cellValue;

        if (typeof rowData[field] === "boolean") {
            if (rowData[field] !== null) {
                cellValue = rowData[field] ? "true" : "false";
            } else {
                cellValue = "N/A";
            }
        } else {
            cellValue = (rowData[field] !== null ? rowData[field] : "N/A");
        }

        return (
            <button
                type="button"
                tabIndex={0}
                onDoubleClick={() => handleCellDoubleClick(rowData, field, rowIndex)}
                onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        handleCellDoubleClick(rowData, field, rowIndex);
                    }
                }}
                style={{
                    cursor: "pointer",
                    background: "none",
                    border: "none",
                    padding: "0",
                    color: "inherit",
                }}
            >
                    {
                        cellValue
                    }
            </button>
        );
    }

    // Hook on agency filter state changes to update device filter;
    // useEffect should be used SPARINGLY due to CASCADE potential
    useEffect(() => {
        const availableAgencies: RsuAgency[] =
            agencyData?.rsus.filter((r) =>
                selectedAgency?.includes(r.agencyName)
            ) || [];
        const availableIds: string[] =
            availableAgencies.flatMap((a) => a.sourceDeviceIds) || [];
        setAvailableRSUs(
            Array.from(deviceLabels.keys()).filter((k) =>
                availableIds.includes(k)
            )
        ); // Use sorted labels
        // let availableLabels: string[] = [];

        setSelectedRSUs(availableIds); // useEffect CASCADES
        console.log(`Selecting devices: ${availableIds.length}`);

        setRsuUpdateKey(uuidv4());
    }, [selectedAgency]);

    function setSelectedDateRanges(e: React.SetStateAction<Date>) {
        setSelectedDate(e);
        const inputDate: Dayjs = dayjs(e.toString());
        setWeekStart(inputDate.toDate());
        setWeekEnd(inputDate.add(6, "day").toDate());
    }

    function isDataLoading(): boolean {
        return dataLoadStatus === LoadingStatus.loading;
    }

    function deviceItemTemplate(option: any): string | undefined {
        return deviceLabels.get(option);
    }

    // Map with collapsible user input tab
    return (
        <div className={styles.RsuCoverage} data-testid="RsuCoverage">
            <Toast ref={toast} />
            <label className="font-semibold">Data Filters</label>
            <div className="flex flex-row gap-2">
                <div className="flex flex-column">
                    <label htmlFor="filter-start-date">Week Start</label>
                    <Calendar
                        // inline style by default
                        id="filter-start-date"
                        value={weekStart ?? selectedDate}
                        onChange={(e: CalendarChangeParams) =>
                            setSelectedDateRanges(e.value as Date)
                        }
                        selectionMode="single"
                        minDate={minDate.toDate()}
                        maxDate={maxDate.toDate()}
                        disabledDays={[1, 2, 3, 4, 5, 6]}
                    />
                </div>
                <div className="flex flex-column">
                    <label htmlFor="filter-end-date">Week End</label>
                    <Calendar id="filter-end-date" value={weekEnd} disabled />
                </div>
                <div className="flex flex-column w-4">
                    <label htmlFor="filter-agency">Agency</label>
                    <MultiSelect
                        id="filter-agency"
                        filter
                        value={selectedAgency}
                        onChange={(e) => {
                            setSelectedAgency(e.value);
                            setSelectedAgencyAll(
                                e.value.length === agencyData?.rsus.length
                            );
                        }}
                        options={agencyData?.rsus
                            .flatMap((r) => r.agencyName)
                            .sort()}
                        selectAll={selectedAgencyAll}
                        placeholder="Select Agencies"
                        maxSelectedLabels={3}
                    />
                </div>
                <div className="flex flex-column w-4">
                    <label htmlFor="filter-device">Device</label>
                    <MultiSelect
                        id="filter-device"
                        filter
                        value={selectedRSUs}
                        onChange={(e) => {
                            setSelectedRSUs(e.value);
                            setSelectedDeviceAll(
                                e.value.length === availableRSUs.length
                            );
                            setRsuUpdateKey(uuidv4());
                        }}
                        options={availableRSUs}
                        selectAll={selectedDeviceAll}
                        placeholder="Select RSUs"
                        maxSelectedLabels={0}
                        itemTemplate={deviceItemTemplate}
                    />
                </div>
                <div className="flex flex-column gap-2">
                    <div className="flex flex-row align-items-center">
                        <Checkbox
                            id="filter-outliers"
                            onChange={(e) =>
                                setIncludeOutliersChecked(e.checked)
                            }
                            checked={includeOutliersChecked}
                        />
                        <label htmlFor="filter-outliers" className="ml-2">
                            Include Outliers
                        </label>
                        <Button
                            title="Help info"
                            icon="pi pi-question-circle"
                            className="p-button p-button-text p-button-icon-only"
                            onClick={(e) => op?.current?.toggle(e)}
                        />
                        <OverlayPanel ref={op}>
                            <p>
                                Outliers are vehicle BSMs located &#177;3
                                standard deviations from the receving RSU
                                polygon centroid.
                                <br />
                                Outliers are included if this option is enabled,
                                excluded if it is disabled.
                            </p>
                        </OverlayPanel>
                    </div>
                </div>
            </div>
            <LoadingOverlay
                aria-label="Loading content..."
                active={isDataLoading()}
                spinner
                text="Loading content..."
            >
                <MapContainer
                    aria-label="Filtered visual content map"
                    center={latLng(configData.mapCenter.lat, configData.mapCenter.lon)}
                    className={styles.MapContainer}
                    key={key}
                    ref={setMap}
                    zoom={8}
                    attributionControl={false}
                    dragging={true}
                    doubleClickZoom={true}
                    scrollWheelZoom={true}
                    zoomControl={true}
                >
                    <TileLayer
                        attribution="&copy; <a href='https://www.openstreetmap.org/copyright'>OpenStreetMap</a> contributors"
                        url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                    />
                    <ScaleControl
                        aria-label="Map scale control"
                        position="bottomleft"
                    />
                    <LayersControl
                        aria-label="Map layer control"
                        position="topleft"
                    >
                        <LayersControl.Overlay
                            aria-label="RSU coverage map layer"
                            checked
                            name={RSUCoverageLegend + "RSU Coverage"}
                        >
                            <LayerGroup ref={setBsmLayer}>
                                <MarkerClusterGroup>
                                    <BasicSafetyMessageGeoJson
                                        bsmData={bsmData}
                                        colorMap={colorMap}
                                        selectedRSU={selectedRSUs}
                                        outliersIncluded={
                                            includeOutliersChecked
                                        }
                                    />
                                </MarkerClusterGroup>
                            </LayerGroup>
                        </LayersControl.Overlay>

                        <LayersControl.Overlay
                            aria-label="RSU map layer"
                            checked
                            name={RSULegend + "RSU"}
                        >
                            <LayerGroup ref={setRsuLayer}>
                                <MarkerClusterGroup>
                                    <RoadSideUnitGeoJson
                                        rsuData={rsuData}
                                        colorMap={colorMap}
                                        selectedRSU={selectedRSUs}
                                        rsuUpdateKey={rsuUpdateKey}
                                    />
                                </MarkerClusterGroup>
                            </LayerGroup>
                        </LayersControl.Overlay>
                    </LayersControl>
                </MapContainer>
                <DataTable
                    value={rsuConfigurations}
                    paginator
                    rows={5}
                    rowsPerPageOptions={[5, 10, 25, 50]}
                    scrollable
                    showGridlines
                    columnResizeMode="expand"
                    resizableColumns
                    stripedRows
                    emptyMessage="No data found"
                    cellSelection
                    selectionMode="single"
                >
                    {columns?.map((col) => (
                        <Column key={col}
                            field={col}
                            header={headerMapping[col] || col}
                            sortable
                            filterField={col}
                            body={(rowData)=> cellBodyTemplate(rowData, {field: col}, rowData.Index)}
                        />
                    ))}
                </DataTable>
                <Dialog
                    header={selectedCell?.field ? headerMapping[selectedCell?.field] : "N/A"}
                    visible={dialogVisible}
                    maximizable
                    resizable
                    style={{ width: "800px" }}
                    onHide={() => setDialogVisible(false)}
                >
                    {selectedCell && (
                        <div>
                            <p>
                                <strong>Value:</strong> {selectedCell.value}
                            </p>
                        </div>
                    )}
                </Dialog>
            </LoadingOverlay>
        </div>
    );
};

export default RsuCoverage;
